import { create } from "zustand";
import {
  createOrder as createOrderApi,
  updateOrder as updateOrderApi,
  changeOrderStatus as changeOrderStatusApi,
  assignOrderDriver as assignOrderDriverApi,
  markOrderPayment as markOrderPaymentApi,
  getOrders,
  getOrderById as getOrderByIdApi,
} from "../services/orderService";
import type {
  Order,
  OrderDetail,
  CreateOrderPayload,
  UpdateOrderPayload,
  MarkOrderPaymentPayload,
  OrderListItem,
} from "../types/models";
import { getApiErrorMessage } from "../utils/apiError";

export interface OrderFilters {
  status: string[];
  customer_id: string | null;
  source: string | null; // 'portal' | null for all (Story 7-7)
  from_date: string | null;
  to_date: string | null;
  date_field: "created_at" | "updated_at" | null;
  search: string | null;
  payment_status: string | null; // 'paid' | 'unpaid' | 'overdue' | null for all (Story 11.6-1)
  page: number;
  limit: number;
}

const defaultFilters: OrderFilters = {
  status: ["created", "to_validate"],
  customer_id: null,
  source: null,
  from_date: null,
  to_date: null,
  date_field: null,
  search: null,
  payment_status: null,
  page: 1,
  limit: 20,
};

interface OrderState {
  orders: OrderListItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  filters: OrderFilters;
  loading: boolean;
  error: string | null;
  orderDetail: OrderDetail | null;
  loadingDetail: boolean;
  errorDetail: string | null;
  /** Count of portal orders in to_validate (for nav badge). Story 7-7 */
  toValidatePortalCount: number;
  createOrder: (payload: CreateOrderPayload) => Promise<Order>;
  updateOrder: (id: string, payload: UpdateOrderPayload) => Promise<Order>;
  changeOrderStatus: (
    id: string,
    payload: { status: string; notes?: string }
  ) => Promise<OrderDetail>;
  assignOrderDriver: (
    id: string,
    payload: { assigned_driver_id: string | null }
  ) => Promise<OrderDetail>;
  markOrderPayment: (
    id: string,
    payload: MarkOrderPaymentPayload
  ) => Promise<OrderDetail>;
  fetchOrders: (params?: Partial<OrderFilters>) => Promise<void>;
  fetchOrderById: (id: string) => Promise<void>;
  fetchToValidatePortalCount: () => Promise<void>;
  clearOrderDetail: () => void;
  setFilters: (filters: Partial<OrderFilters>) => void;
  clearFilters: () => void;
  setOrders: (orders: OrderListItem[]) => void;
  clearError: () => void;
}

export const useOrderStore = create<OrderState>((set, get) => ({
  orders: [],
  total: 0,
  page: 1,
  limit: 20,
  pages: 0,
  filters: defaultFilters,
  loading: false,
  error: null,
  orderDetail: null,
  loadingDetail: false,
  errorDetail: null,
  toValidatePortalCount: 0,

  createOrder: async (payload: CreateOrderPayload) => {
    set({ loading: true, error: null });
    try {
      const order = await createOrderApi(payload);
      set({ loading: false, error: null });
      return order;
    } catch (e) {
      set({ loading: false, error: getApiErrorMessage(e, "Failed to create order") });
      throw e;
    }
  },

  updateOrder: async (id: string, payload: UpdateOrderPayload) => {
    set({ loading: true, error: null });
    try {
      const order = await updateOrderApi(id, payload);
      // Update orderDetail if it's the same order
      const { orderDetail } = get();
      if (orderDetail && orderDetail.id === id) {
        // Refresh order detail after update
        await get().fetchOrderById(id);
      }
      // Refresh orders list
      await get().fetchOrders();
      set({ loading: false, error: null });
      return order;
    } catch (e) {
      set({ loading: false, error: getApiErrorMessage(e, "Failed to update order") });
      throw e;
    }
  },

  changeOrderStatus: async (id: string, payload: { status: string; notes?: string }) => {
    set({ loading: true, error: null });
    try {
      const detail = await changeOrderStatusApi(id, payload);
      set({ orderDetail: detail, loading: false, error: null });
      await get().fetchOrders();
      return detail;
    } catch (e) {
      set({ loading: false, error: getApiErrorMessage(e, "Failed to change order status") });
      throw e;
    }
  },

  assignOrderDriver: async (id: string, payload: { assigned_driver_id: string | null }) => {
    set({ loading: true, error: null });
    try {
      const detail = await assignOrderDriverApi(id, payload);
      set({ orderDetail: detail, loading: false, error: null });
      await get().fetchOrders();
      return detail;
    } catch (e) {
      set({ loading: false, error: getApiErrorMessage(e, "Failed to assign driver") });
      throw e;
    }
  },

  markOrderPayment: async (id: string, payload: MarkOrderPaymentPayload) => {
    set({ loading: true, error: null });
    try {
      const detail = await markOrderPaymentApi(id, payload);
      set({ orderDetail: detail, loading: false, error: null });
      await get().fetchOrders();
      return detail;
    } catch (e) {
      set({ loading: false, error: getApiErrorMessage(e, "Failed to update order payment") });
      throw e;
    }
  },

  fetchOrders: async (params) => {
    set({ loading: true, error: null });
    const { filters } = get();
    const merged = params ? { ...filters, ...params } : filters;
    if (params) set({ filters: merged });
    try {
      const res = await getOrders({
        status: merged.status.length ? merged.status : undefined,
        customer_id: merged.customer_id ?? undefined,
        source: merged.source ?? undefined,
        from_date: merged.from_date ?? undefined,
        to_date: merged.to_date ?? undefined,
        date_field: merged.date_field ?? undefined,
        search: merged.search ?? undefined,
        payment_status: merged.payment_status ?? undefined,
        page: merged.page,
        limit: merged.limit,
      });
      set({
        orders: res.items,
        total: res.total,
        page: res.page,
        limit: res.limit,
        pages: res.pages,
        loading: false,
        error: null,
      });
    } catch (e) {
      set({ loading: false, error: getApiErrorMessage(e, "Failed to load orders") });
    }
  },

  fetchOrderById: async (id) => {
    set({ loadingDetail: true, errorDetail: null });
    try {
      const detail = await getOrderByIdApi(id);
      set({ orderDetail: detail, loadingDetail: false, errorDetail: null });
    } catch (e) {
      set({
        orderDetail: null,
        loadingDetail: false,
        errorDetail: getApiErrorMessage(e, "Failed to load order"),
      });
    }
  },

  fetchToValidatePortalCount: async () => {
    try {
      const res = await getOrders({
        status: ["to_validate"],
        source: "portal",
        page: 1,
        limit: 1,
      });
      set({ toValidatePortalCount: res.total });
    } catch (e) {
      console.warn("Orders badge: failed to fetch to-validate portal count", e);
      set({ toValidatePortalCount: 0 });
    }
  },

  clearOrderDetail: () => set({ orderDetail: null, errorDetail: null }),

  setFilters: (filters) => {
    set((state) => ({ filters: { ...state.filters, ...filters } }));
  },

  clearFilters: () => set({ filters: { ...defaultFilters } }),

  setOrders: (orders) => set({ orders }),
  clearError: () => set({ error: null }),
}));
